import { useState, useEffect } from 'react';
import { Modal, Input, Select, Checkbox } from '@/shared/components/ui';
import { Save, X, Type, Hash, Mail, Calendar, ToggleRight } from 'lucide-react';
import { JsonField } from './JsonFieldBuilder';
import styles from '../TipoUsuarioModals.module.css';

interface AddEditFieldModalProps {
    isOpen: boolean;
    field?: JsonField;
    existingFieldNames: string[];
    onClose: () => void;
    onSave: (field: JsonField) => void;
}

export const AddEditFieldModal = ({
    isOpen,
    field,
    existingFieldNames,
    onClose,
    onSave
}: AddEditFieldModalProps) => {
    const [formData, setFormData] = useState<JsonField>({
        nombre: '',
        tipo: 'string',
        requerido: false,
        descripcion: '',
        longitudMaxima: undefined,
        mensajeError: ''
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const isEditing = !!field;

    // Cargar datos si estamos editando
    useEffect(() => {
        if (field) {
            setFormData(field);
        } else {
            setFormData({
                nombre: '',
                tipo: 'string',
                requerido: false,
                descripcion: '',
                longitudMaxima: undefined,
                mensajeError: ''
            });
        }
        setErrors({});
    }, [field, isOpen]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Validar nombre
        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre del campo es requerido';
        } else if (!/^[a-z0-9_]+$/.test(formData.nombre)) {
            newErrors.nombre = 'Solo letras minúsculas, números y guiones bajos';
        } else if (!isEditing && existingFieldNames.includes(formData.nombre)) {
            newErrors.nombre = 'Ya existe un campo con este nombre';
        }

        // Validar descripción
        if (formData.descripcion && formData.descripcion.length > 200) {
            newErrors.descripcion = 'Máximo 200 caracteres';
        }

        // Validar longitud máxima
        if (formData.longitudMaxima !== undefined && formData.longitudMaxima <= 0) {
            newErrors.longitudMaxima = 'Debe ser mayor a 0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            // Limpiar campos opcionales vacíos
            const cleanedField: JsonField = {
                ...formData,
                descripcion: formData.descripcion?.trim() || undefined,
                mensajeError: formData.mensajeError?.trim() || undefined,
                longitudMaxima: formData.longitudMaxima || undefined
            };

            onSave(cleanedField);
        }
    };

    const handleChange = (field: keyof JsonField, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Limpiar error del campo al cambiar
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const tipoOptions = [
        { value: 'string', label: 'Texto', icon: <Type size={16} /> },
        { value: 'number', label: 'Número', icon: <Hash size={16} /> },
        { value: 'email', label: 'Email', icon: <Mail size={16} /> },
        { value: 'date', label: 'Fecha', icon: <Calendar size={16} /> },
        { value: 'boolean', label: 'Sí/No', icon: <ToggleRight size={16} /> },
    ];

    const showLongitudMaxima = ['string', 'number', 'email'].includes(formData.tipo);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEditing ? 'Editar Campo' : 'Nuevo Campo'}
            size="md"
            closeOnOverlayClick={false}
            headerActions={
                <>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.saveButton}`}
                        onClick={handleSubmit}
                        title="Guardar campo"
                        aria-label={isEditing ? "Actualizar campo personalizado" : "Guardar campo personalizado"}
                    >
                        <Save size={18} strokeWidth={2.5} />
                    </button>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.cancelButton}`}
                        onClick={onClose}
                        title="Cancelar"
                        aria-label="Cancelar edición de campo"
                    >
                        <X size={18} strokeWidth={2.5} />
                    </button>
                </>
            }
        >
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className={styles.modalContent}>
                <Input
                    label="Nombre del campo *"
                    value={formData.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value.toLowerCase())}
                    error={errors.nombre}
                    placeholder="ejemplo: telefono"
                    disabled={isEditing}
                />

                <div className={styles.formRow}>
                    <div className={styles.formRowFlex}>
                        <Select
                            label="Tipo de dato *"
                            value={formData.tipo}
                            onChange={(value: string | number) => handleChange('tipo', value as JsonField['tipo'])}
                            options={tipoOptions}
                        />
                    </div>
                    <div className={styles.formRowCheckbox}>
                        <Checkbox
                            label="Campo requerido"
                            checked={formData.requerido}
                            onChange={(checked) => handleChange('requerido', checked)}
                        />
                    </div>
                </div>

                <Input
                    label="Descripción"
                    value={formData.descripcion || ''}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    error={errors.descripcion}
                    placeholder="Descripción del campo para ayudar al usuario"
                />

                {showLongitudMaxima && (
                    <Input
                        type="number"
                        label="Longitud máxima"
                        value={formData.longitudMaxima?.toString() || ''}
                        onChange={(e) => handleChange('longitudMaxima', e.target.value ? parseInt(e.target.value) : undefined)}
                        error={errors.longitudMaxima}
                        placeholder="Ej: 15, 100, 500"
                    />
                )}

                <Input
                    label="Mensaje de error personalizado"
                    value={formData.mensajeError || ''}
                    onChange={(e) => handleChange('mensajeError', e.target.value)}
                    placeholder="Ej: El teléfono es obligatorio"
                    disabled={!formData.nombre.trim()}
                />
            </form>
        </Modal>
    );
};
